import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  PermissionStatus,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import router from '@/core/router';
import { logAnalyticsEvent, refreshAnalyticsSession } from '@/core/analytics/analytics';
import {
  AnalyticsEventName,
  AppSessionSourceNames,
  SectionEntrySourceNames,
} from '@/core/analytics/types';
import { getSectionNameFromPath } from '@/shared/tools/get-screen-from-url';

export const CapacitorPushNotificationsPlugin = {
  install() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    PushNotifications.requestPermissions().then((result: PermissionStatus) => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        console.log('Push notifications. Access granted');
        PushNotifications.register();
      } else {
        console.error('Push notifications. Access denied');
      }
    });

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration',
      (token: Token) => {
        console.log('Push registration success, token: ' + token.value);

        localStorage.setItem('pushNotificationsToken', token.value);
      },
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError',
      (error: unknown) => {
        console.error('Error on registration: ' + JSON.stringify(error));
      },
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push received: ' + JSON.stringify(notification));
      },
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      async (action: ActionPerformed) => {
        console.log('Push action performed: ' + JSON.stringify(action));

        await refreshAnalyticsSession(AppSessionSourceNames.Push);

        if (action.notification.data.tag) {
          router.push({
            path: action.notification.data.tag,
          });

          const sectionName = getSectionNameFromPath(action.notification.data.tag);

          logAnalyticsEvent({
            name: AnalyticsEventName.SectionEntry,
            params: {
              section_name: sectionName,
              source: SectionEntrySourceNames.Push,
            },
          });
        }
      },
    );
  },
};
